import { supabase } from '../supabaseClient';

export type BlockType =
  | 'short_text'
  | 'bullets'
  | 'quote'
  | 'fact'
  | 'timeline'
  | 'related_songs'
  | 'reflection_prompt'
  | 'external_link'
  | 'share_prompt'
  | 'cta_action'
  | 'divider';

export type ModalType = 'look_deeper' | 'one_key_insight' | 'explore_connections' | 'take_it_further';

export interface ModalBlock {
  id: string;
  block_type: BlockType;
  title: string | null;
  body: string | null;
  url: string | null;
  xp_value: number;
  metadata: Record<string, any>;
  enabled: boolean;
  order_index?: number;
}

export interface ModalContent {
  modal_type: ModalType;
  blocks: ModalBlock[];
}

export interface AllModalContent {
  look_deeper: ModalBlock[];
  one_key_insight: ModalBlock[];
  explore_connections: ModalBlock[];
  take_it_further: ModalBlock[];
}

export async function getModalContent(
  songId: string,
  civicLevelKey: string,
  modalType: ModalType
): Promise<ModalBlock[]> {
  try {
    // Check for song-specific modal overrides (custom blocks)
    const { data: overrides, error: overridesError } = await supabase
      .from('song_modal_overrides')
      .select(`
        order_index,
        enabled,
        block:modal_blocks(*)
      `)
      .eq('song_id', songId)
      .eq('modal_type_id', modalType)
      .eq('enabled', true)
      .order('order_index');

    if (!overridesError && overrides && overrides.length > 0) {
      return overrides.map((o: any, idx) => ({
        ...o.block,
        order_index: o.order_index ?? idx,
      }));
    }

    // Fallback to legacy song fields if no custom blocks
    const { data: song } = await supabase
      .from('songs')
      .select('deep_insight, quick_fact, context_in_time, related_song_ids')
      .eq('id', songId)
      .maybeSingle();

    if (song) {
      return getLegacyFallbackBlocks(modalType, song);
    }

    return [];
  } catch (err) {
    console.error('[getModalContent] Error:', err);
    return [];
  }
}

function getLegacyFallbackBlocks(modalType: ModalType, song: any): ModalBlock[] {
  const blocks: ModalBlock[] = [];

  if (modalType === 'look_deeper' && song.deep_insight) {
    // Parse the deep_insight text into separate sections
    const parsedSections = parseLegacyDeepInsight(song.deep_insight);

    parsedSections.forEach((section, index) => {
      blocks.push({
        id: `legacy-deep-insight-${index}`,
        block_type: 'short_text',
        title: section.title,
        body: section.body,
        url: null,
        xp_value: 0,
        metadata: {},
        enabled: true,
        order_index: index,
      });
    });

    if (song.context_in_time) {
      blocks.push({
        id: 'legacy-context',
        block_type: 'timeline',
        title: 'Context in Time',
        body: song.context_in_time,
        url: null,
        xp_value: 0,
        metadata: {},
        enabled: true,
        order_index: parsedSections.length,
      });
    }
  }

  if (modalType === 'one_key_insight' && song.quick_fact) {
    blocks.push({
      id: 'legacy-quick-fact',
      block_type: 'fact',
      title: null,
      body: song.quick_fact,
      url: null,
      xp_value: 0,
      metadata: {},
      enabled: true,
      order_index: 0,
    });
  }

  if (modalType === 'explore_connections' && song.related_song_ids && song.related_song_ids.length > 0) {
    blocks.push({
      id: 'legacy-related-songs',
      block_type: 'related_songs',
      title: null,
      body: null,
      url: null,
      xp_value: 0,
      metadata: { song_ids: song.related_song_ids },
      enabled: true,
      order_index: 0,
    });
  }

  return blocks;
}

export async function getAllModalContentForSong(
  songId: string,
  civicLevelKey: string
): Promise<AllModalContent> {
  const [lookDeeper, oneKeyInsight, exploreConnections, takeItFurther] = await Promise.all([
    getModalContent(songId, civicLevelKey, 'look_deeper'),
    getModalContent(songId, civicLevelKey, 'one_key_insight'),
    getModalContent(songId, civicLevelKey, 'explore_connections'),
    getModalContent(songId, civicLevelKey, 'take_it_further'),
  ]);

  return {
    look_deeper: lookDeeper,
    one_key_insight: oneKeyInsight,
    explore_connections: exploreConnections,
    take_it_further: takeItFurther,
  };
}

export async function saveModalContent(
  songId: string,
  civicLevelKey: string,
  modalType: ModalType,
  blocks: Partial<ModalBlock>[]
): Promise<{ success: boolean; error?: any }> {
  try {
    console.log('[saveModalContent] Saving:', { songId, modalType, blockCount: blocks.length });

    // Delete existing modal overrides for this song+modal combination
    const { error: deleteError } = await supabase
      .from('song_modal_overrides')
      .delete()
      .eq('song_id', songId)
      .eq('modal_type_id', modalType);

    if (deleteError) {
      console.error('[saveModalContent] Delete error:', deleteError);
    }

    const blockInserts = [];
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];

      let blockId = block.id;
      if (!blockId || blockId.startsWith('temp-') || blockId.startsWith('legacy-')) {
        const { data: newBlock, error: blockError } = await supabase
          .from('modal_blocks')
          .insert({
            block_type: block.block_type,
            title: block.title,
            body: block.body,
            url: block.url,
            xp_value: block.xp_value || 0,
            metadata: block.metadata || {},
            enabled: block.enabled !== false,
          })
          .select()
          .maybeSingle();

        if (blockError || !newBlock) {
          throw new Error(`Failed to create block: ${blockError?.message}`);
        }
        blockId = newBlock.id;
      } else {
        await supabase
          .from('modal_blocks')
          .update({
            title: block.title,
            body: block.body,
            url: block.url,
            metadata: block.metadata || {},
            enabled: block.enabled !== false,
          })
          .eq('id', blockId);
      }

      blockInserts.push({
        song_id: songId,
        modal_type_id: modalType,
        block_id: blockId,
        order_index: i,
        enabled: true,
      });
    }

    if (blockInserts.length > 0) {
      const { error: insertError } = await supabase
        .from('song_modal_overrides')
        .insert(blockInserts);

      if (insertError) {
        console.error('[saveModalContent] Insert error:', insertError);
        throw new Error(`Failed to create overrides: ${insertError.message}`);
      }
    }

    console.log('[saveModalContent] Save complete');
    return { success: true };
  } catch (err) {
    console.error('[saveModalContent] Error:', err);
    return { success: false, error: err };
  }
}

export async function deleteModalContent(
  songId: string,
  civicLevelKey: string,
  modalType: ModalType
): Promise<{ success: boolean; error?: any }> {
  try {
    const { error } = await supabase
      .from('song_modal_overrides')
      .delete()
      .eq('song_id', songId)
      .eq('modal_type_id', modalType);

    if (error) {
      throw new Error(`Failed to delete modal content: ${error.message}`);
    }

    return { success: true };
  } catch (err) {
    console.error('[deleteModalContent] Error:', err);
    return { success: false, error: err };
  }
}

export async function migrateLegacyModalFields(songId: string): Promise<{ success: boolean; migrated: string[] }> {
  const migrated: string[] = [];

  try {
    const { data: song } = await supabase
      .from('songs')
      .select('deep_insight, quick_fact, context_in_time, related_song_ids')
      .eq('id', songId)
      .maybeSingle();

    if (!song) {
      return { success: false, migrated: [] };
    }

    // Migrate deep_insight to look_deeper modal
    if (song.deep_insight) {
      const existing = await getModalContent(songId, '', 'look_deeper');
      if (existing.length === 0) {
        const blocks: Partial<ModalBlock>[] = [];

        const sections = parseLegacyDeepInsight(song.deep_insight);
        sections.forEach((section, idx) => {
          blocks.push({
            id: `temp-deep-${idx}`,
            block_type: 'short_text',
            title: section.title,
            body: section.body,
            url: null,
            xp_value: 0,
            metadata: {},
            enabled: true,
          });
        });

        if (song.context_in_time) {
          blocks.push({
            id: 'temp-context',
            block_type: 'timeline',
            title: 'Context in Time',
            body: song.context_in_time,
            url: null,
            xp_value: 0,
            metadata: {},
            enabled: true,
          });
        }

        await saveModalContent(songId, '', 'look_deeper', blocks);
        migrated.push('look_deeper');
      }
    }

    // Migrate quick_fact to one_key_insight modal
    if (song.quick_fact) {
      const existing = await getModalContent(songId, '', 'one_key_insight');
      if (existing.length === 0) {
        await saveModalContent(songId, '', 'one_key_insight', [
          {
            id: 'temp-fact',
            block_type: 'fact',
            title: null,
            body: song.quick_fact,
            url: null,
            xp_value: 0,
            metadata: {},
            enabled: true,
          },
        ]);
        migrated.push('one_key_insight');
      }
    }

    // Migrate related_song_ids to explore_connections modal
    if (song.related_song_ids && song.related_song_ids.length > 0) {
      const existing = await getModalContent(songId, '', 'explore_connections');
      if (existing.length === 0) {
        await saveModalContent(songId, '', 'explore_connections', [
          {
            id: 'temp-related',
            block_type: 'related_songs',
            title: null,
            body: null,
            url: null,
            xp_value: 0,
            metadata: { song_ids: song.related_song_ids },
            enabled: true,
          },
        ]);
        migrated.push('explore_connections');
      }
    }

    return { success: true, migrated };
  } catch (err) {
    console.error('[migrateLegacyModalFields] Error:', err);
    return { success: false, migrated };
  }
}

function parseLegacyDeepInsight(text: string): Array<{ title: string; body: string }> {
  const sections: Array<{ title: string; body: string }> = [];

  if (!text || text.trim().length === 0) {
    return sections;
  }

  // Check if this contains "What It's Really About" section header
  if (text.includes("What It's Really About")) {
    // Try to extract content after the header
    // Match "What It's Really About" followed by optional whitespace and newlines, then capture the rest
    const whatItsReallyAboutMatch = text.match(/What It's Really About[\s\n]+([\s\S]+)$/);
    if (whatItsReallyAboutMatch && whatItsReallyAboutMatch[1]) {
      sections.push({
        title: "What It's Really About",
        body: whatItsReallyAboutMatch[1].trim(),
      });
    } else {
      // Header exists but no content after it - just use everything after the header
      const parts = text.split("What It's Really About");
      if (parts.length > 1 && parts[1].trim()) {
        sections.push({
          title: "What It's Really About",
          body: parts[1].trim(),
        });
      }
    }
  } else {
    // No header - wrap all content with the header
    sections.push({
      title: "What It's Really About",
      body: text.trim(),
    });
  }

  return sections;
}
